import React, { Fragment, useState } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { reduxForm, getFormValues, Field } from 'redux-form';
import arrayMove from 'array-move';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';

import { AddButton, BlockButton, CardElement, SwitchInput, TextInput, Row, Modal, StatusChip, EmptyState } from '../../_Components';

const optionWord = (n) => {
    const m = n % 100;
    const d = n % 10;
    if (m > 10 && m < 20) return 'опций';
    if (d === 1) return 'опция';
    if (d >= 2 && d <= 4) return 'опции';
    return 'опций';
};

const nextOptionValue = (options) => {
    const nums = (options || []).map((item) => Number(item.value)).filter((value) => !Number.isNaN(value));
    return nums.length ? Math.max(...nums) + 1 : 1;
};

const DeletedBlockField = ({ input, isSaving }) => (
    <BlockButton
        status={input.value ? 'Disabled' : 'Active'}
        isSaving={isSaving}
        handleBlockClick={() => input.onChange(!input.value)}
    />
);

const EditFormElement = () => (
    <Fragment>
        <Row>
            <Field name="label" required={true} component={TextInput} label={`Надпись`} style={{ width: 450 }} />
            <Field name="value" required={true} component={TextInput} label={`Значение`} style={{ width: 100 }} />
        </Row>
        <Field name="deleted" component={DeletedBlockField} />
    </Fragment>
);

const ComboboxFormAdd = () => (
    <Row>
        <Field name="name" required={true} component={TextInput} label={`Надпись`} style={{ width: 250 }} />
    </Row>
);

const PasteTextarea = ({ input }) => (
    <textarea
        {...input}
        rows={8}
        style={{
            width: '100%',
            border: '1px solid var(--line)',
            borderRadius: 6,
            padding: '9px 10px',
            fontFamily: 'var(--mono)',
            fontSize: 12,
            lineHeight: 1.6,
            resize: 'vertical',
        }}
    />
);

const PasteForm = () => (
    <div>
        <p style={{ fontSize: 11.5, color: 'var(--muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
            Одна опция в строке. Код можно указать через вертикальную черту — если его нет, назначим следующий свободный.
        </p>
        <Field name="list" component={PasteTextarea} />
    </div>
);

const GrabHandle = () => (
    <span className="pc-orow__grab">
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
    </span>
);

const Grab = SortableHandle(GrabHandle);

const OptionRow = ({ option, selected, isTask, onSelect, onEdit, onToggle, grab }) => {
    const subs = (option.sub || []).length;
    return (
        <div
            className={`pc-orow${selected ? ' is-sel' : ''}${option.deleted ? ' is-off' : ''}`}
            onClick={onSelect}
        >
            {grab ? <Grab /> : <span className="pc-orow__grab" />}
            <span className="pc-orow__lbl">{option.label}</span>
            {option.deleted ? <StatusChip>выключена</StatusChip> : null}
            {isTask ? (
                <span className={`pc-orow__sub${subs ? ' has' : ''}`}>{subs ? `▸ ${subs}` : '—'}</span>
            ) : null}
            <span className="pc-orow__code">{option.value}</span>
            <span className="pc-orow__acts">
                <IconButton
                    size="small"
                    title={option.deleted ? 'Включить' : 'Выключить'}
                    onClick={(event) => { event.stopPropagation(); onToggle(); }}
                >
                    {option.deleted
                        ? <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />
                        : <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                </IconButton>
                <IconButton
                    size="small"
                    title="Редактировать"
                    onClick={(event) => { event.stopPropagation(); onEdit(); }}
                >
                    <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </span>
        </div>
    );
};

const SortableOptionRow = SortableElement((props) => <OptionRow {...props} grab />);

const SortableOptionList = SortableContainer(({ items, activeOptionsKey, isTask, onSelect, onEdit, onToggle }) => (
    <div>
        {items.map((option, index) => (
            <SortableOptionRow
                key={`${option.value}-${index}`}
                index={index}
                option={option}
                selected={activeOptionsKey === index}
                isTask={isTask}
                onSelect={() => onSelect(index)}
                onEdit={() => onEdit(option, index)}
                onToggle={() => onToggle(index)}
            />
        ))}
    </div>
));

const Comboboxes = ({ formValues, change, ...props }) => {
    const [activeNodeKey, setActiveNodeKey] = useState(0);
    const [activeOptionsKey, setActiveOptionsKey] = useState(0);
    const [editIndex, setEditIndex] = useState(null);
    const [subIndex, setSubIndex] = useState(null);
    const [openAddCombobox, setOpenAddCombobox] = useState(false);
    const [editOption, setEditOption] = useState(false);
    const [editSub, setEditSub] = useState(false);
    const [pasteOpen, setPasteOpen] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [query, setQuery] = useState('');
    const [optQuery, setOptQuery] = useState('');
    const [optFilter, setOptFilter] = useState('all');
    const [quickLabel, setQuickLabel] = useState('');
    const [selectedSubKey, setSelectedSubKey] = useState(0);

    if (!formValues) {
        return null;
    }
    const comboboxList = formValues.combobox || [];
    const q = query.trim().toLowerCase();
    const visible = comboboxList
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !q || `${item.name || ''}`.toLowerCase().includes(q));
    const branching = visible.filter(({ item }) => item.isTask);
    const plain = visible.filter(({ item }) => !item.isTask);
    const combobox = comboboxList[activeNodeKey];
    const options = (combobox && combobox.options) || [];
    const oq = optQuery.trim().toLowerCase();
    const optionEntries = options
        .map((option, index) => ({ option, index }))
        .filter(({ option }) => {
            if (optFilter === 'on' && option.deleted) return false;
            if (optFilter === 'off' && !option.deleted) return false;
            if (oq && !`${option.label || ''} ${option.value || ''}`.toLowerCase().includes(oq)) return false;
            return true;
        });
    const canSort = optFilter === 'all' && !oq;
    const selectedOption = options[activeOptionsKey];
    const subtopics = (selectedOption && selectedOption.sub) || [];
    const subOffCount = subtopics.filter((item) => item.deleted).length;
    const usedFields = (formValues.fields || []).filter((field) => (
        combobox && combobox._id && String(field.combobox) === String(combobox._id)
    ));

    const patchCombobox = (index, next) => {
        const list = [...(formValues.combobox || [])];
        if (!list[index]) {
            return;
        }
        list[index] = { ...list[index], ...next };
        change('combobox', list);
    };

    const addCombobox = () => setOpenAddCombobox(true);
    const closeAddCombobox = () => setOpenAddCombobox(false);

    const saveCombobox = (form) => {
        const newCombobox = [...(formValues.combobox || []), { deleted: false, options: [], ...form }];
        change('combobox', newCombobox);
        setActiveNodeKey(newCombobox.length - 1);
        setActiveOptionsKey(0);
        setOpenAddCombobox(false);
    };

    const setActiveNode = (index) => {
        setActiveNodeKey(index);
        setActiveOptionsKey(0);
        setSelectedSubKey(0);
        setOptQuery('');
        setOptFilter('all');
    };

    const editOptionClick = (form, index) => {
        setEditForm(form);
        setEditIndex(index);
        setEditOption(true);
    };

    const editSubClick = (form, index) => {
        setEditForm(form);
        setSubIndex(index);
        setEditSub(true);
    };

    const closeEdit = () => {
        setEditOption(false);
        setEditSub(false);
        setPasteOpen(false);
        setEditIndex(null);
        setEditForm(null);
    };

    const onSortabeOptions = (newData) => {
        patchCombobox(activeNodeKey, { options: newData });
    };

    const patchSubs = (subs) => {
        const next = [...options];
        if (!next[activeOptionsKey]) {
            return;
        }
        next[activeOptionsKey] = { ...next[activeOptionsKey], sub: subs };
        patchCombobox(activeNodeKey, { options: next });
    };

    const saveOption = (form) => {
        if (!combobox) {
            return;
        }
        const next = [...options];
        if (editIndex >= 0) {
            next[editIndex] = form;
        } else {
            next.push({ sub: [], ...form });
        }
        patchCombobox(activeNodeKey, { options: next });
        setEditOption(false);
        setEditIndex(null);
        setEditForm(null);
    };

    const saveSub = (form) => {
        if (!combobox?.options?.[activeOptionsKey]) {
            return;
        }
        const subs = [...subtopics];
        if (subIndex >= 0) {
            subs[subIndex] = form;
        } else {
            subs.push({ deleted: false, ...form });
        }
        patchSubs(subs);
        setEditSub(false);
        setEditForm(null);
        setSubIndex(null);
    };

    const toggleOption = (index) => {
        const next = [...options];
        next[index] = { ...next[index], deleted: !next[index].deleted };
        patchCombobox(activeNodeKey, { options: next });
    };

    const toggleSub = (index) => {
        const subs = [...subtopics];
        if (!subs[index]) {
            return;
        }
        subs[index] = { ...subs[index], deleted: !subs[index].deleted };
        patchSubs(subs);
    };

    const addQuickOption = () => {
        const label = quickLabel.trim();
        if (!label || !combobox) {
            return;
        }
        const next = [...options, { label, value: nextOptionValue(options), sub: [], deleted: false }];
        patchCombobox(activeNodeKey, { options: next });
        setQuickLabel('');
        setActiveOptionsKey(next.length - 1);
    };

    const savePaste = (form) => {
        const lines = String((form && form.list) || '').split('\n').map((line) => line.trim()).filter(Boolean);
        if (!lines.length || !combobox) {
            setPasteOpen(false);
            return;
        }
        let cursor = nextOptionValue(options);
        const added = lines.map((line) => {
            const [rawLabel, rawValue] = line.split('|').map((part) => part && part.trim());
            const label = rawLabel && rawLabel.includes(':')
                ? (rawLabel.split(':').slice(1).join(':').trim() || rawLabel)
                : rawLabel;
            let value = rawValue;
            if (value === undefined || value === '') {
                value = cursor;
                cursor += 1;
            }
            return { label, value, sub: [], deleted: false };
        });
        patchCombobox(activeNodeKey, { options: [...options, ...added] });
        setPasteOpen(false);
    };

    const renderDictGroup = (title, rows) => {
        if (!rows.length) {
            return null;
        }
        return (
            <Fragment>
                <div className="pc-dl__sec">{title}</div>
                {rows.map(({ item, index }) => (
                    <button
                        key={index}
                        type="button"
                        className={`pc-dl__row${index === activeNodeKey ? ' is-cur' : ''}`}
                        onClick={() => setActiveNode(index)}
                    >
                        <span className="pc-dl__t">{item.name}</span>
                        <span className="pc-dl__n">{(item.options || []).length}</span>
                    </button>
                ))}
            </Fragment>
        );
    };

    return (
        <Fragment>
            <div className="pc-fill">
                <div className="pc-dicts">
                    <CardElement
                        fill
                        content={(
                            <div className="pc-panel-body">
                                <div className="pc-toolbar">
                                    <TextField
                                        size="small"
                                        placeholder="Поиск"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        style={{ flex: 1, minWidth: 0 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{ fontSize: 16, color: 'var(--muted-2)' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <IconButton size="small" color="primary" onClick={addCombobox} title="Новый справочник">
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </div>
                                <div className="pc-tblwrap">
                                    <div className="pc-dl">
                                        {renderDictGroup('Ветвят сценарий', branching)}
                                        {renderDictGroup('Обычные', plain)}
                                    </div>
                                </div>
                            </div>
                        )}
                    />

                    <CardElement
                        fill
                        title={combobox ? (combobox.name || 'Справочник') : 'Опции'}
                        subheader={combobox ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2, pl: 1 }}>
                                <StatusChip tone="mono">{options.length} {optionWord(options.length)}</StatusChip>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        pl: 2,
                                        ml: 0.5,
                                        borderLeft: '1px solid var(--line-2)',
                                    }}
                                >
                                    <Field
                                        name={`combobox[${activeNodeKey}].isTask`}
                                        label="Тема разговора"
                                        component={SwitchInput}
                                    />
                                </Box>
                            </Box>
                        ) : null}
                        action={combobox ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Button size="small" variant="outlined" onClick={() => setPasteOpen(true)}>
                                    Вставить списком
                                </Button>
                                <AddButton label="Опция" handleAddClick={editOptionClick} variant="contained" />
                            </Box>
                        ) : null}
                        content={(
                            <div className="pc-panel-body">
                                {combobox ? (
                                    <Fragment>
                                        <div className="pc-toolbar">
                                            <TextField
                                                size="small"
                                                placeholder="Поиск по опциям и кодам"
                                                value={optQuery}
                                                onChange={(e) => setOptQuery(e.target.value)}
                                                style={{ flex: 1, minWidth: 150 }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon sx={{ fontSize: 16, color: 'var(--muted-2)' }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            {['all', 'on', 'off'].map((id) => (
                                                <button
                                                    key={id}
                                                    type="button"
                                                    className={`pc-fbtn${optFilter === id ? ' is-on' : ''}`}
                                                    onClick={() => setOptFilter(id)}
                                                >
                                                    {id === 'all' ? 'Все' : id === 'on' ? 'Включены' : 'Выключены'}
                                                </button>
                                            ))}
                                            <span className="pc-rescount">
                                                {optionEntries.length !== options.length
                                                    ? `показано ${optionEntries.length} из ${options.length}`
                                                    : `${options.filter((item) => !item.deleted).length} вкл · ${options.filter((item) => item.deleted).length} выкл`}
                                            </span>
                                        </div>
                                        <div className="pc-tblwrap">
                                            {canSort ? (
                                                <SortableOptionList
                                                    items={options}
                                                    useDragHandle
                                                    helperClass="pc-orow"
                                                    activeOptionsKey={activeOptionsKey}
                                                    isTask={!!combobox.isTask}
                                                    onSelect={(index) => {
                                                        setActiveOptionsKey(index);
                                                        setSelectedSubKey(0);
                                                    }}
                                                    onEdit={editOptionClick}
                                                    onToggle={toggleOption}
                                                    onSortEnd={({ oldIndex, newIndex }) => {
                                                        onSortabeOptions(arrayMove(options, oldIndex, newIndex));
                                                        setActiveOptionsKey(newIndex);
                                                    }}
                                                />
                                            ) : (
                                                optionEntries.map(({ option, index }) => (
                                                    <OptionRow
                                                        key={`${option.value}-${index}`}
                                                        option={option}
                                                        selected={activeOptionsKey === index}
                                                        isTask={!!combobox.isTask}
                                                        onSelect={() => {
                                                            setActiveOptionsKey(index);
                                                            setSelectedSubKey(0);
                                                        }}
                                                        onEdit={() => editOptionClick(option, index)}
                                                        onToggle={() => toggleOption(index)}
                                                    />
                                                ))
                                            )}
                                            <div className="pc-newrow">
                                                <AddIcon sx={{ fontSize: 16, color: 'var(--muted-2)' }} />
                                                <input
                                                    value={quickLabel}
                                                    placeholder="Новая опция — введите название и нажмите Enter"
                                                    onChange={(e) => setQuickLabel(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            addQuickOption();
                                                        }
                                                    }}
                                                />
                                                <span className="pc-newrow__k">Enter</span>
                                            </div>
                                        </div>
                                        {usedFields.length > 0 && (
                                            <div className="pc-toolbar" style={{ borderTop: '1px solid var(--line-2)', borderBottom: 0 }}>
                                                <span className="pc-rescount">
                                                    используется в {usedFields.length} {usedFields.length === 1 ? 'поле' : 'полях'}
                                                </span>
                                            </div>
                                        )}
                                    </Fragment>
                                ) : (
                                    <EmptyState
                                        icon={<ListAltOutlinedIcon fontSize="small" />}
                                        title="Справочник не выбран"
                                        description="Выберите справочник слева или создайте новый."
                                    />
                                )}
                            </div>
                        )}
                    />

                    <CardElement
                        fill
                        title="Подтемы"
                        subheader={<StatusChip tone="mono">{
                            subOffCount
                                ? `${subtopics.length - subOffCount} вкл · ${subOffCount} выкл`
                                : subtopics.length
                        }</StatusChip>}
                        action={combobox && combobox.isTask && selectedOption
                            ? <AddButton label="Добавить" handleAddClick={editSubClick} />
                            : null}
                        content={(
                            <div className="pc-panel-body">
                                {combobox && combobox.isTask && selectedOption ? (
                                    <Fragment>
                                        <div className="pc-subhead">
                                            <div className="pc-subhead__k">Тема</div>
                                            <div className="pc-subhead__v">{selectedOption.label}</div>
                                        </div>
                                        <div className="pc-tblwrap">
                                            {subtopics.length ? (
                                                <SortableOptionList
                                                    items={subtopics}
                                                    useDragHandle
                                                    helperClass="pc-orow"
                                                    activeOptionsKey={selectedSubKey}
                                                    isTask={false}
                                                    onSelect={setSelectedSubKey}
                                                    onEdit={editSubClick}
                                                    onToggle={toggleSub}
                                                    onSortEnd={({ oldIndex, newIndex }) => {
                                                        patchSubs(arrayMove(subtopics, oldIndex, newIndex));
                                                        setSelectedSubKey(newIndex);
                                                    }}
                                                />
                                            ) : (
                                                <EmptyState
                                                    title="Нет подтем"
                                                    description="Добавьте подтему для выбранной темы."
                                                />
                                            )}
                                        </div>
                                        <div className="pc-note">
                                            Выключенные подтемы остаются в этом списке. Оператор увидит только включённые — и только после выбора этой темы в карточке.
                                        </div>
                                    </Fragment>
                                ) : (
                                    <EmptyState
                                        icon={<ListAltOutlinedIcon fontSize="small" />}
                                        title={combobox && combobox.isTask ? 'Тема не выбрана' : 'Нет подтем'}
                                        description={combobox && combobox.isTask
                                            ? 'Выберите опцию в середине, чтобы править её подтемы.'
                                            : 'Подтемы есть только у справочника с ролью «тема разговора».'}
                                    />
                                )}
                            </div>
                        )}
                    />
                </div>
            </div>

            {editOption && (
                <Modal
                    width={640}
                    title={editForm ? 'Редактировать' : 'Добавить'}
                    open={editOption}
                    handleSaveClick={saveOption}
                    handleCloseClick={closeEdit}
                    fields={<EditFormElement />}
                    initial={editForm}
                />
            )}
            {editSub && (
                <Modal
                    width={640}
                    title={editForm ? 'Редактировать' : 'Добавить'}
                    open={editSub}
                    handleSaveClick={saveSub}
                    handleCloseClick={closeEdit}
                    fields={<EditFormElement />}
                    initial={editForm}
                />
            )}
            {openAddCombobox && (
                <Modal
                    title="Добавить"
                    open={openAddCombobox}
                    handleSaveClick={saveCombobox}
                    handleCloseClick={closeAddCombobox}
                    fields={<ComboboxFormAdd />}
                />
            )}
            {pasteOpen && (
                <Modal
                    width="md"
                    title="Вставить списком"
                    open={pasteOpen}
                    handleSaveClick={savePaste}
                    handleCloseClick={() => setPasteOpen(false)}
                    fields={<PasteForm />}
                />
            )}
        </Fragment>
    );
};

const mapStateToProps = (state) => {
    const formValues = getFormValues('project')(state);
    return { formValues };
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'project',
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: true,
    }),
);

export default enhance(Comboboxes);
